import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { sanityClient, urlFor } from "../lib/sanityClient";
import { fadeUp, scaleIn, stagger, viewport } from "../lib/motion";
import "../styles/IntroSection.css";

const QUERY = `*[_type == "introSection"][0]{
  brandHeading, brandLogos, subheading, headingMain,
  headingHighlight, introImage, paragraphs
}`;

const ParagraphText = ({ text }) => {
  if (!text) return null;

  // Replace literal \n sequences from Sanity with actual newlines; **phrase** renders bold
  const parts = text.replace(/\\n/g, '\n').split(/\*\*(.+?)\*\*/g);
  return (
    <span style={{ whiteSpace: "pre-line" }}>
      {parts.map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part))}
    </span>
  );
};

const IntroSection = () => {
  const [data, setData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    console.log("IntroSection: Fetching data from Sanity...");
    sanityClient.fetch(QUERY).then(res => {
      console.log("IntroSection Data:", res);
      setData(res);
    }).catch(err => console.error("IntroSection Fetch Error:", err));
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const logos = (data?.brandLogos || []).map((l) => urlFor(l.logo).url());

  return (
    <section className="intro-section" id="about">
      {data && (
        <>
          {/* Brand Trust */}
          <motion.div
            className="brand-trust"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={stagger(0.12)}
          >
            <motion.p className="brand-heading" variants={fadeUp}>
              {data.brandHeading}
            </motion.p>

        {isMobile ? (
          <div className="marquee-wrapper">
            <motion.div
              className="marquee-content"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ repeat: Infinity, duration: 24, ease: "linear" }}
            >
              {[...logos, ...logos].map((src, i) => (
                <img src={src} alt="brand" className="marquee-logo" key={i} />
              ))}
            </motion.div>
          </div>
        ) : (
          <motion.div className="brand-logos-wrapper" variants={stagger(0.1)}>
            <div className="brand-logos">
              {logos.map((src, i) => (
                <motion.img
                  src={src}
                  alt="brand"
                  key={i}
                  className="brand-logo"
                  variants={scaleIn}
                />
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Intro Content */}
      <motion.div
        className="intro-content"
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        variants={stagger(0.15)}
      >
        <motion.div
          className="intro-radial-bg"
          animate={{ opacity: [0.5, 0.75, 0.5], scale: [0.96, 1.06, 0.96] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Left Column */}
        <motion.div className="intro-left" variants={fadeUp}>
          <p className="intro-subheading">{data.subheading}</p>
          <h2 className="intro-heading">
            {data.headingMain}
            <span className="highlighted-gpt">{data.headingHighlight}</span>
          </h2>
          <motion.div className="intro-visual" variants={scaleIn}>
            <img src={urlFor(data.introImage).url()} alt="Visual" />
          </motion.div>
        </motion.div>

        <div className="intro-divider"></div>

        {/* Right Column */}
        <motion.div className="intro-right" variants={stagger(0.15)}>
          {(data.paragraphs || []).map((p, index) => (
            <motion.p className="intro-paragraph" variants={fadeUp} key={p._key || index}>
              <ParagraphText text={p.text} />
            </motion.p>
          ))}
        </motion.div>
      </motion.div>
      </>
      )}
    </section>
  );
};

export default IntroSection;
